import { type AssetExposure, isOptionSymbol } from '@/features/home/lib/home-exposure'
import { Exchange } from '@/types/generated/common/types'

/**
 * The daily brief's datapoints: which numbers from the Data widget's sources matter for what the
 * user holds today. Each kind of datapoint has a relevance rule (why it matters for these
 * positions); the highest scoring ones fill the brief, and the AI writes its comment about
 * exactly those. Pure (tested in __tests__/home-datapoints.test.ts).
 */

export type DatapointKind =
  | 'impliedMove' // options-implied 1σ move to the next weekly expiry
  | 'funding' // the perp's funding rate: what holding it costs or pays
  | 'marketMove' // 24h price change and open interest on the asset's perp
  | 'ivPercentile' // how rich today's implied vol is vs the last 90 days
  | 'maxPain' // the strike option holders lose most at, for the next expiry
  | 'etfFlows' // spot ETF net flows, the institutional bid
  | 'fedwatch' // the next FOMC meeting's priced outcome
  | 'skew' // 25-delta risk reversal and put/call ratio: how options are positioned
  | 'liquidationLevels' // where liquidations pile up near the price (Hyperliquid)

export interface Datapoint {
  id: string
  kind: DatapointKind
  /** The coin it is about; null for macro (FedWatch). */
  asset: string | null
  score: number
  /** Why it was picked, as a message key under trade.home.brief.why. */
  why: DatapointWhy
  /** For funding/market move: the perp to read live. */
  perp?: { symbol: string; exchange: Exchange }
}

export type DatapointWhy =
  | 'nearLiquidation'
  | 'leveraged'
  | 'holdPerp'
  | 'holdOptions'
  | 'topHolding'
  | 'holding'
  | 'macro'
  | 'watchlist'

/** Deribit lists options (and so implied-move / IV data) on these. */
export const OPTION_ASSETS = new Set(['BTC', 'ETH', 'SOL'])
/** Spot ETFs report flows for these. */
export const ETF_ASSETS = new Set(['BTC', 'ETH', 'SOL', 'HYPE'])

export const BRIEF_SIZE = 6

/** With nothing open, the brief watches the market leaders instead. */
const WATCHLIST: Datapoint[] = [
  { id: 'impliedMove:BTC', kind: 'impliedMove', asset: 'BTC', score: 90, why: 'watchlist' },
  {
    id: 'marketMove:BTC',
    kind: 'marketMove',
    asset: 'BTC',
    score: 85,
    why: 'watchlist',
    perp: { symbol: 'BTC-PERPETUAL', exchange: Exchange.EXCHANGE_DERIBIT },
  },
  { id: 'etfFlows:BTC', kind: 'etfFlows', asset: 'BTC', score: 80, why: 'watchlist' },
  { id: 'fedwatch', kind: 'fedwatch', asset: null, score: 75, why: 'macro' },
  {
    id: 'marketMove:ETH',
    kind: 'marketMove',
    asset: 'ETH',
    score: 70,
    why: 'watchlist',
    perp: { symbol: 'ETH-PERPETUAL', exchange: Exchange.EXCHANGE_DERIBIT },
  },
  { id: 'ivPercentile:BTC', kind: 'ivPercentile', asset: 'BTC', score: 65, why: 'watchlist' },
]

/**
 * The instrument to read the asset's market from: a perp the user holds, else another non-option
 * position, else the asset's Deribit perp (the venue that lists BTC/ETH/SOL options).
 */
export function marketInstrumentFor(
  exposure: AssetExposure
): { symbol: string; exchange: Exchange } | undefined {
  if (exposure.perps[0]) return exposure.perps[0]
  const linear = exposure.positions.find((risk) => !isOptionSymbol(risk.position.symbol))
  if (linear) return { symbol: linear.position.symbol, exchange: linear.position.exchange }
  if (exposure.asset === 'BTC' || exposure.asset === 'ETH') {
    return { symbol: `${exposure.asset}-PERPETUAL`, exchange: Exchange.EXCHANGE_DERIBIT }
  }
  if (OPTION_ASSETS.has(exposure.asset)) {
    return { symbol: `${exposure.asset}_USDC-PERPETUAL`, exchange: Exchange.EXCHANGE_DERIBIT }
  }
  return undefined
}

export function selectDatapoints(
  exposures: readonly AssetExposure[],
  /** Kinds the user asked to see less of. */
  hidden: ReadonlySet<DatapointKind> = new Set()
): Datapoint[] {
  const held = exposures.filter((exposure) => exposure.positions.length > 0)
  if (held.length === 0) return WATCHLIST.filter((datapoint) => !hidden.has(datapoint.kind))

  const picks: Datapoint[] = []
  held.forEach((exposure, rank) => {
    const { asset } = exposure
    // A bigger holding matters more, but a small risky one still gets a look.
    const weight = 0.6 + 0.4 * exposure.share - rank * 0.05
    const nearLiquidation = exposure.positions.some((p) => p.reasons.includes('nearLiquidation'))
    const leveraged = exposure.positions.some((p) => p.reasons.includes('highLeverage'))
    const hasOptionData = OPTION_ASSETS.has(asset)
    const perp = exposure.perps[0]

    if (hasOptionData) {
      // The market's expected move next to how far the user's liquidation sits is the one
      // number a leveraged newcomer most needs to see.
      picks.push({
        id: `impliedMove:${asset}`,
        kind: 'impliedMove',
        asset,
        score: (nearLiquidation ? 100 : leveraged ? 90 : 70) * weight,
        why: nearLiquidation ? 'nearLiquidation' : leveraged ? 'leveraged' : 'holding',
      })
    }
    if (exposure.hasPerp && perp) {
      picks.push({
        id: `funding:${asset}`,
        kind: 'funding',
        asset,
        score: (leveraged ? 85 : 70) * weight,
        why: 'holdPerp',
        perp,
      })
    }
    const market = marketInstrumentFor(exposure)
    if (market) {
      picks.push({
        id: `marketMove:${asset}`,
        kind: 'marketMove',
        asset,
        score: (rank === 0 ? 75 : 55) * weight,
        why: rank === 0 ? 'topHolding' : 'holding',
        perp: market,
      })
    }
    if (hasOptionData) {
      picks.push({
        id: `ivPercentile:${asset}`,
        kind: 'ivPercentile',
        asset,
        score: (exposure.hasOption ? 88 : 40) * weight,
        why: exposure.hasOption ? 'holdOptions' : 'holding',
      })
    }
    if (hasOptionData) {
      picks.push({
        id: `skew:${asset}`,
        kind: 'skew',
        asset,
        score: (exposure.hasOption ? 78 : leveraged ? 62 : 42) * weight,
        why: exposure.hasOption ? 'holdOptions' : leveraged ? 'leveraged' : 'holding',
      })
    }
    if (nearLiquidation || leveraged) {
      // A cascade through a cluster of other traders' liquidations can reach the user's own.
      picks.push({
        id: `liquidationLevels:${asset}`,
        kind: 'liquidationLevels',
        asset,
        score: (nearLiquidation ? 95 : 80) * weight,
        why: nearLiquidation ? 'nearLiquidation' : 'leveraged',
      })
    }
    if (hasOptionData && exposure.hasOption) {
      picks.push({
        id: `maxPain:${asset}`,
        kind: 'maxPain',
        asset,
        score: 80 * weight,
        why: 'holdOptions',
      })
    }
    if (ETF_ASSETS.has(asset)) {
      picks.push({
        id: `etfFlows:${asset}`,
        kind: 'etfFlows',
        asset,
        score: (rank === 0 ? 60 : 45) * weight,
        why: rank === 0 ? 'topHolding' : 'holding',
      })
    }
  })
  picks.push({ id: 'fedwatch', kind: 'fedwatch', asset: null, score: 50, why: 'macro' })

  return picks
    .filter((datapoint) => !hidden.has(datapoint.kind))
    .sort((a, b) => b.score - a.score)
    .slice(0, BRIEF_SIZE)
}
